import type { ScreeningProviderConfiguration } from "@repo/models";

import { GridFiltersController } from "@repo/baseCloud";
import { PushErrorNotification, PushSuccessNotification } from "@repo/helpers";
import { Button, ButtonTypes, FilterDropdown, TextBox } from "@repo/ui";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import BackgroundChecksApi from "../../../api/backgroundChecksApi";
import { QUERY_KEYS, useFilterItemQuery } from "../../../api/queries";
import * as Queries from "../../../api/queries";

interface Props {
	configuration: Partial<ScreeningProviderConfiguration>;
	onSave: () => void;
}

export function ConfigurationForm({ configuration, onSave }: Props) {
	const [form, setForm] = useState<Partial<ScreeningProviderConfiguration>>(configuration);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isTesting, setIsTesting] = useState(false);

	const isNew = !configuration.marketerId;

	const [marketers] = useFilterItemQuery([Queries.MARKETERS], () => GridFiltersController.getMarketers());

	const { data: screeningProviders } = useQuery({
		queryKey: [QUERY_KEYS.BackgroundChecks.Configurations, "Providers"],
		queryFn: () => BackgroundChecksApi.getScreeningProviders(true),
	});

	const { data: products } = useQuery({
		queryKey: [QUERY_KEYS.BackgroundChecks.Configurations, "Products"],
		queryFn: () => BackgroundChecksApi.getScreeningProducts(),
	});

	const handleChange = useCallback((value: any, id: string) => {
		setForm((prev) => ({ ...prev, [id]: value }));
	}, []);

	const handleTestConnection = async () => {
		setIsTesting(true);
		try {
			const error = await BackgroundChecksApi.testConfiguration(form);
			if (error) {
				PushErrorNotification(error);
			} else {
				PushSuccessNotification("Connection test successful");
			}
		} catch {
			PushErrorNotification("An error occurred while testing the connection");
		} finally {
			setIsTesting(false);
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setIsSubmitting(true);
		try {
			await BackgroundChecksApi.saveConfiguration(form);
			PushSuccessNotification("Configuration saved successfully");
			onSave();
		} catch {
			PushErrorNotification("Failed to save configuration");
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<form onSubmit={handleSubmit} className="flex flex-col gap-2 p-2">
			<FilterDropdown
				id="marketerId"
				objectName="Marketer"
				values={marketers.data ?? []}
				valueProp="id"
				textProp="value"
				value={form.marketerId}
				changeHandler={handleChange}
				disabled={!isNew}
				fullLength
				allOption={false}
				allTitle="Marketers..."
			/>
			<FilterDropdown
				id="screeningProviderId"
				objectName="Screening Provider"
				values={screeningProviders ?? []}
				valueProp="ScreeningProviderId"
				textProp="Name"
				value={form.screeningProviderId}
				changeHandler={handleChange}
				disabled={!isNew}
				fullLength
				allOption={false}
				allTitle="Providers..."
			/>
			<TextBox
				id="organizationName"
				label="Organization Name"
				value={form.organizationName}
				changeHandler={handleChange}
			/>
			<TextBox id="username" label="Username" value={form.username} changeHandler={handleChange} />
			<TextBox
				id="password"
				label="Password"
				type="password"
				value={form.password}
				changeHandler={handleChange}
			/>
			<FilterDropdown
				id="productIds"
				objectName="Configured Products"
				values={products ?? []}
				valueProp="ScreeningProductId"
				textProp="Name"
				value={form.productIds}
				changeHandler={handleChange}
				isMulti
				fullLength
				allOption={false}
				allTitle="Products..."
			/>

			<div className="mt-4 flex justify-end gap-2">
				<Button type="button" onClick={handleTestConnection} disabled={isTesting || isSubmitting}>
					{isTesting ? "Testing..." : "Test Connection"}
				</Button>
				<Button types={[ButtonTypes.Primary]} type="submit" disabled={isSubmitting || isTesting}>
					{isSubmitting ? "Saving..." : "Save"}
				</Button>
			</div>
		</form>
	);
}
